import { motion } from 'framer-motion'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React, { FC, useMemo } from 'react'

import ImageByWidthDevice from '@/components/ImageByWidthDevice'
import { introBottomVariants, introLeftVariants, introRightVariants } from '@/config/const'
import { getNews } from '@/services/news'
import { NewsListRequest } from '@/types/news'
import { formatEpoch } from '@/utils/formatter'
import { useAppSelector } from '@/utils/hooks'

const PressRelease: FC = () => {
  const router = useRouter()
  const { t } = useTranslation()

  const requestList: NewsListRequest = useMemo(() => {
    return {
      params: { skip: 0, limit: 3 },
      headers: { 'Accept-Language': String(router.locale) },
    }
  }, [])

  const { data: news } = useAppSelector(getNews.select(requestList))

  if (news?.data?.items && news?.data?.items.length > 0) {
    return (
      <div className='relative flex h-full w-full items-center justify-center overflow-hidden bg-white'>
        <div className='container flex flex-col gap-6 py-6 sm:py-8 lg:gap-12.5 lg:py-12.5'>
          <div className='flex items-center justify-between'>
            <motion.h2
              className='flex-1 text-center text-2xl font-bold leading-normal sm:text-left sm:text-[32px]'
              initial='hide'
              whileInView='show'
              viewport={{ once: true }}
              variants={introLeftVariants}
            >
              {t('home:pressRelease.title')}
            </motion.h2>

            <Link href='/media/press'>
              <motion.button
                className='hidden w-fit cursor-pointer items-center gap-x-2 rounded-full bg-red px-5 py-3 text-base leading-[18.77px] text-white sm:flex'
                initial='hide'
                whileInView='show'
                viewport={{ once: true }}
                variants={introRightVariants}
              >
                <span className='font-work font-bold'>{t('common:viewMore')}</span>
                <span className='kek-arrow-right text-xl' />
              </motion.button>
            </Link>
          </div>

          <motion.div
            className='grid grid-cols-2 justify-start gap-6 lg:grid-cols-6 lg:gap-7.5'
            initial='hide'
            whileInView='show'
            viewport={{ once: true }}
            variants={introBottomVariants}
          >
            {news?.data.items.map((press) => (
              <div
                key={press.xid}
                className='border-form-boder group relative col-span-2 overflow-hidden rounded-xl border before:absolute before:bottom-0 before:left-0 before:z-10 before:h-1 before:w-0 before:bg-orange before:transition-all before:duration-300 hover:border-transparent hover:shadow-card hover:before:w-full'
              >
                <div className='h-[205px] w-full overflow-hidden'>
                  <ImageByWidthDevice
                    images={press.imageFiles}
                    alt={press.imageFiles.desktop.fileName ?? ''}
                    className='h-[205px] w-full object-cover object-center transition-all duration-300 group-hover:scale-125'
                  />
                </div>

                <div className='flex w-full flex-col gap-3.5 bg-white px-4 py-3.5'>
                  <h4 className='line-clamp-2 break-all text-sm font-semibold leading-normal tracking-[-0.6%]'>
                    {press.title}
                  </h4>
                  <p className='line-clamp-2 break-all text-xs leading-normal text-gray-2'>{press.shortDescription}</p>

                  <div className='flex items-center justify-between border-t border-t-accent-light/10 pt-3'>
                    <p className='text-xs leading-normal text-gray-2'>
                      {formatEpoch(press.createdAt, 'EEEE, dd LLLL yyyy | T', router.locale)}
                    </p>

                    <Link href={`/media/press/${press.slug}`}>
                      <span className='kek-arrow-right cursor-pointer text-[24px]' />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>

          <Link
            href='/media/press'
            className='flex w-full cursor-pointer items-center justify-center gap-x-2 rounded-full bg-red px-5 py-3 text-base leading-[18.77px] text-white sm:hidden'
          >
            <span className='font-work font-bold'>{t('common:viewMore')}</span>
            <span className='kek-arrow-right text-xl' />
          </Link>
        </div>
      </div>
    )
  }
  return null
}

export default PressRelease
